import i18n from 'i18next';
import personApi from '../apiV2/personApi';
import moment from 'moment';
import { SHORT_DATE_TIME_FORMAT } from "../contsants";

export const noValidator = () => ({
    validator: () => {
        return true;
    },
    errorMessage: ""
});

export const maxLengthValidator = (maxLength) => ({
    validator: (value) => {
        if (value && value.length > maxLength) {
            return [i18n.t("common:validation.maxLengthSymbols", { maxLength: maxLength })];
        }
    }
});

export const regexpValidator = (regexp) => ({
    validator: (value) => {
        if (!(regexp instanceof RegExp) && typeof regexp !== 'string') {
            return [i18n.t("common:validation.invalidRegularExpressionFormat")];
        }

        if (typeof regexp === 'string') {
            regexp = new RegExp(regexp);
        }

        if (!regexp.test(value)) {
            return [i18n.t("common:validation.invalidFormat")];
        }
    }
});

export const urlValidator = () => ({
    validator: (value) => {
        if (!/((http|ftp|https):\/\/)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/.test(value)) {
            return [i18n.t("common:validation.invalidURLFormat")];
        }
    }
});

//Integer select
export const required = () => ({
    validator: (values) => {
        if ((values && values.length === 0) || values === 0) {
            return [i18n.t("common:validation.required", { field: '{{}}' })];
        }
        if (values && values.constructor === Object && Object.keys(values).length === 0) {
            return [i18n.t("common:validation.required", { field: '{{}}' })];
        }
    }
});

export const notBlank = () => ({
    validator: (value) => {
        if (!value || value.trim() === '') {
            return [i18n.t("common:validation.notBlank")];
        }
    }
});

export const lengthValidator = (length) => ({
    validator: (value) => {
        if (value.length !== length) {
            return [i18n.t("common:validation.lengthDigits", { length: length })];
        }
    }
});

export const numberValidator = (canBeEmpty, maxDecimalPlaces, additionalAllowedValues = []) => ({
    validator: (value) => {
        if ((canBeEmpty && value === '') || additionalAllowedValues.indexOf(value) >= 0) {
            return true;
        }

        if (typeof value === 'string') {
            value = value.replace(/,/g, '.');

            if (maxDecimalPlaces) {
                let splitResult = value.toString().split(".");
                let decimalPart = splitResult[1];
                if (decimalPart && decimalPart.length > maxDecimalPlaces) {
                    return [i18n.t("common:validation.maxDecimalSymbols", { maxLength: maxDecimalPlaces })];
                }
            }
        }

        if (isNaN(value - parseFloat(value))) {
            return [i18n.t("common:validation.number")];
        }
    }
});

export const numberRangeValidator = (from, to, allowComma = false) => ({
    validator: (value, field) => {
        if (field.required || value.length > 0) {
            if (allowComma && typeof value === 'string') {
                value = value.replace(/,/g, '.');
            }
            if (isNaN(value - parseFloat(value))) {
                return [i18n.t("common:validation.number")];
            }
            if (parseFloat(from) > parseFloat(value) || parseFloat(value) > parseFloat(to)) {
                return [i18n.t("common:validation.numberRange", { from: from, to: to })];
            }
        }
    }
});

export const positiveValidator = () => ({
    validator: (value) => {
        if (parseFloat(value) < 0) {
            return [i18n.t("common:validation.positiveNumber")];
        }
    }
});

export const decimalValidator = () => ({
    validator: (value) => {
        if (value !== undefined && value.length > 0) {
            if (!/^\d{0,}[.,]{0,1}\d{1,3}$/.test(value)) {
                return [i18n.t("common:validation.number") + " (##,###)"];
            }
        }
    }
});

export const emailValidator = () => ({
    validator: (value) => {
        // eslint-disable-next-line
        const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (value && !re.test(String(value).toLowerCase())) {
            return [i18n.t("common:validation.email")];
        }
    }
});

export const phoneValidator = () => ({
    validator: (value) => {
        if (value && !value.match(/^(\(?\+\d{1,3}\)?)?\s?[\d\s]+$/)) {
            return [i18n.t("common:validation.number")];
        }
    }
});

export const estonianIdValidator = () => ({
    validator: (value) => {
        if (value.length !== 11) {
            return [i18n.t("common:validation.estonianId")];
        }

        let control = /* istanbul ignore next */ (value) => {
            const multiplier1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 1];
            const multiplier2 = [3, 4, 5, 6, 7, 8, 9, 1, 2, 3];

            let mod;
            let total = 0;

            for (let i = 0; i < 10; i++) {
                total += value.charAt(i) * multiplier1[i];
            }

            mod = total % 11;

            total = 0;

            if (10 === mod) {
                for (let i = 0; i < 10; i++) {
                    total += value.charAt(i) * multiplier2[i];
                }
                mod = total % 11;
                if (10 === mod) {
                    mod = 0;
                }
            }
            return mod;
        };

        if (control(value) !== parseInt(value.charAt(10))) {
            return [i18n.t("common:validation.estonianId")];
        }

        let year = Number(value.substr(1, 2));
        let month = Number(value.substr(3, 2));
        let day = Number(value.substr(5, 2));

        let birthDate = /* istanbul ignore next */ (value) => {
            let year = parseInt(value.substring(1, 3));
            let month = parseInt(value.substring(3, 5).replace(/^0/, '')) - 1;
            let day = value.substring(5, 7).replace(/^0/, '');
            let firstNumber = value.charAt(0);

            if (firstNumber === '1' || firstNumber === '2') {
                year += 1800;
            } else if (firstNumber === '3' || firstNumber === '4') {
                year += 1900;
            } else if (firstNumber === '5' || firstNumber === '6') {
                year += 2000;
            } else {
                year += 2100;
            }

            return new Date(year, month, day);
        };

        if (year !== birthDate(value).getFullYear() % 100 &&
            birthDate(value).getMonth() + 1 !== month &&
            day !== birthDate(value).getDate()) {
            return [i18n.t("common:validation.estonianId")];
        }
    }
});

export const userPersonCodeValidator = () => ({
    validator: (value, field, model) => {
        if (model.id === 0) {
            return new Promise((resolve) => {
                personApi.validate(value).then(result => {
                    resolve(result.type === 'success');
                });
            })
        } else {
            return true;
        }

    },
    errorMessage: i18n.t("common:validation.userAlreadyExists"),
});

export const contactValidator = () => ({
    validator: (value, field, model) => {
        if (model.typeId === "1") {
            return emailValidator().validator(model.value);
        }

        if (model.typeId === "2") {
            return phoneValidator().validator(model.value);
        }
    }
});

export const alphabetValidator = () => ({
    validator: (value) => {
        const letters = /^[A-Za-zÜüÕõÄäÖöŽžŠš-]+$/;
        if (value && !letters.test(String(value))) {
            return [i18n.t("common:validation.alphabet")];
        }
    }
});

export const dateDiffValidator = (startName, endName, maxDiff = '') => ({
    validator: (value, field, model) => {
        if (model[endName] !== "") {
            let end = parseInt(moment(model[endName], 'DD.MM.YYYY HH:mm').format("X"));
            let start = parseInt(moment(model[startName], 'DD.MM.YYYY HH:mm').format("X"));

            if (start > end) {
                return [i18n.t("common:validation.diffDates")];
            }

            if (maxDiff) {
                let [, count, period] = /([\d]*)([\w]+)/.exec(maxDiff),
                    start = moment(model[startName], 'DD.MM.YYYY HH:mm').add(count, period),
                    end = moment(model[endName], 'DD.MM.YYYY HH:mm');
                if (end.isAfter(start)) {
                    return [i18n.t("common:validation.diffDatesRangeTooBig", { count, period: i18n.t("common:dates." + period) })];
                }
            }
        }
    }
});

export const atLeastOneNotBlankValidator = (names) => ({
    validator: (value, field, model) => {
        let atLeastOneNotBlank = false;
        console.log(model, names);
        for (let name of names) {
            if (model[name] !== "" && model[name].length > 0 && model[name] !== 0) {
                atLeastOneNotBlank = true;
                break;
            }
        }

        if (!atLeastOneNotBlank) {
            return [i18n.t("common:validation.required", { field: '{{}}' })];
        }
    }
});

export const datePastFutureValidator = (type) => ({
    validator: (value, field) => {
        if (field.required || value.length > 0) {
            let onlyDate = Boolean(value.trim().length <= 10),
                date = moment(value, 'DD.MM.YYYY' + (!onlyDate && ' HH:mm')),
                types = Array.isArray(type) ? type : [type];

            if (types.indexOf('future') === -1 && ((!onlyDate && date > moment())
                || (onlyDate && date > moment().set({ hour: 23, minute: 59, second: 59, millisecond: 59 })))) {
                return [i18n.t("common:validation.futureDateNotAllowed")];
            } else if (types.indexOf('past') === -1) {
                if (types.indexOf('date-only') !== -1) {
                    if (!onlyDate && date < moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 })) {
                        return [i18n.t("common:validation.pastDateNotAllowed")];
                    }
                } else if ((!onlyDate && date < moment())
                    || (onlyDate && date < moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }))) {
                    return [i18n.t("common:validation.pastDateNotAllowed")];
                }
            }
        }
    }
});

export const dateValidator = (onlyDate, additionalAllowedValues = []) => ({
    validator: (value, field) => {
        if (additionalAllowedValues.indexOf(value) >= 0) {
            return true;
        }

        if (field.required || value.length > 0) {
            const dateTime = splitString(value, " ");
            const date = dateTime[0] !== undefined ? dateTime[0].match(/^\s*(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.((?:19|20)\d{2})\s*$/) : null;
            const time = dateTime[1] !== undefined ? dateTime[1].match(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/) : null;

            // Validate time
            if ((!onlyDate && dateTime.length !== 2)
                || (!onlyDate && time === null)
                || (dateTime[1] !== undefined && time == null)) {
                return [i18n.t("common:validation.date")];
            }

            // Validate date format
            if ((onlyDate && dateTime.length !== 1) || date === null) {
                return [i18n.t("common:validation.date")];
            }
        }
    }
});

export const dateRangeValidator = (minDate, maxDate, minDateFieldName = '') => ({
    validator: (value, field, model) => {
        if (field.required || value.length > 0) {
            let onlyDate = Boolean(value.trim().length <= 10),
                date = moment(value, 'DD.MM.YYYY' + (!onlyDate && ' HH:mm'));
            minDate = minDateFieldName !== '' ? model[minDateFieldName] : minDate;

            if (minDate) {
                if ((!onlyDate && date < moment(minDate))
                    || (onlyDate && date < moment(minDate, 'DD.MM.YYYY').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }))) {
                    return [i18n.t("common:validation.dateFrom", {
                        from: onlyDate ? minDate : moment(minDate).format(SHORT_DATE_TIME_FORMAT)
                    })];
                }
            }
        }
    }
});

export const dateReg = (reg) => ({
    validator: (value, field) => {
        if (field.required || value.length > 0) {
            const date = value.match(reg);

            // Validate date format
            if (date === null) {
                return [i18n.t("common:validation.date")];
            }
        }
    }
});

export const roleCodeValidator = () => ({
    validator: (value) => {
        if (value && !value.match(/^[\w\d_]*$/)) {
            return [i18n.t("common:validation.wrongRoleFormat")];
        }
    }
});

const splitString = (value, separator) => {
    return value.split(separator);
};
